import {cellNumberFactory, Email, emailRegex, Header, mobileRegex, setup as setUpUtilites, TextField} from "js/util";
import moment from "moment";
import _ from "underscore";

let configuration = {
    columnWording: null,
    defaultDateFormat: null
};

function initializer(config) {
    configuration = config;
    setUpUtilites();
}

function calculateStats(filteredTail, mobPos, emPos, bDayPos) {
    const countFieldByValidity = (position, type) => _.extend(
        {valid: 0, invalid: 0},
        position ? _.chain(filteredTail)
                .filter(row => row.length > position)
                .map(row => row[position])
                .countBy(field => field.valid && field.type === type ? "valid" : "invalid")
                .value()
            : {});

    const countriesCount = mobPos ? _.chain(filteredTail)
            .filter(row => row.length > mobPos && row[mobPos].type === "mobile" && row[mobPos].valid)
            .map(row => row[mobPos])
            .countBy(mobile => mobile.countryCode)
            .value()
        : {};

    const definedInt = number => !isNaN(parseInt(number));

    const validContacts = _.filter(filteredTail, row =>
        (definedInt(emPos) && row[emPos] && row[emPos].valid) || (definedInt(mobPos) && row[mobPos] && row[mobPos])).length;
    const mobileStats = countFieldByValidity(mobPos, "mobile");
    const emailStats = countFieldByValidity(emPos, "email");
    const dateStats = countFieldByValidity(bDayPos, "birthday");
    return {countriesCount, validContacts, mobileStats, emailStats, dateStats};
}

function* calculator(initialData) {
    const [first, ...tail] = initialData;

    const headers = first
        .map((h, i) => new Header(h).set("index", i));

    const cleanTail = tail
        .map(row => row.map(cell => cell ? cell.trim() : ""))
        .filter(row => row.reduce((acc, cell) => String.prototype.concat(acc, cell)) !== "");

    //if any headers are missing, fill with auto-generated
    const maxRowLengthDiff = cleanTail.map(row => row.length).reduce((memo, x) => Math.max(memo, x)) - headers.length;
    if (maxRowLengthDiff > 0)
        headers.push(
            ...new Array(maxRowLengthDiff)
                .fill("")
                .map((v, k) => new Header(configuration.columnWording + (k + 1)))
        );

    yield {progress: 1, message: 'fileup.cleanedentries'};

    const counterLength = headers.length;
    const mobileCount = new Array(counterLength).fill(0);
    const emailCount = new Array(counterLength).fill(0);
    const bDayCount = new Array(counterLength).fill(0);
    const defaultDateFormat = configuration.defaultDateFormat;
    const chunkSize = 100;
    const validatedTail = [];
    const chunkedTail = _.chain(cleanTail).groupBy((el, i) => Math.floor(i / chunkSize)).toArray().value();
    const totalChunks = chunkedTail.length;
    let currentChunk = 1;
    for (let c of chunkedTail) {
        const mappedChunk = c.map(row => {
            row.invalid = true;
            return row.map((cell, colNum) => {
                if (cell.length && headers[colNum]) {
                    headers[colNum].empty = false;
                    if (mobileRegex.test(cell)) {
                        const cellNumber = cellNumberFactory(cell);
                        if (cellNumber.isPresent() && cellNumber.get().valid) {
                            mobileCount[colNum]++;
                            delete row.invalid;
                        }
                        return cellNumber.orElse(cell);
                    } else if (moment(cell, defaultDateFormat, true).isValid()) {
                        bDayCount[colNum]++;
                        cell = new TextField(cell, true, "birthday");
                    } else if (emailRegex.test(cell)) {
                        cell = new Email(cell, true);
                        emailCount[colNum]++;
                        delete row.invalid;
                    }
                    else
                        cell = new TextField(cell);
                }
                return cell;
            });
        });
        validatedTail.push(...mappedChunk);
        yield {progress: 1 + currentChunk++ / totalChunks, message: 'fileup.validating'};
    }

    //determine which column has the most values of each field
    const maxMob = Math.max(...mobileCount);
    const maxEm = Math.max(...emailCount);
    const maxBir = Math.max(...bDayCount);

    const rejectEmpty = _.partial(_.reject, _, (v, i) => headers[i].empty);
    const filteredTail = validatedTail.map(rejectEmpty);
    const filteredMobileCnt = rejectEmpty(mobileCount);
    const filteredEmailCnt = rejectEmpty(emailCount);
    const filteredBDayCnt = rejectEmpty(bDayCount);
    const usefulHeaders = _.reject(headers, x => x.empty);
    const multipleHeaders = _.chain(usefulHeaders).countBy(tf => tf.text).pairs().filter(h => h[1] > 1).object().value();

    const mobPos = maxMob === 0 ? undefined : filteredMobileCnt.indexOf(maxMob);
    const emPos = maxEm === 0 ? undefined : filteredEmailCnt.indexOf(maxEm);
    const bDayPos = maxBir === 0 ? undefined : filteredBDayCnt.indexOf(maxBir);

    if (maxMob > 0) usefulHeaders[mobPos].type = "mobile";
    if (maxEm > 0) usefulHeaders[emPos].type = "email";
    if (maxBir > 0) usefulHeaders[bDayPos].type = "date";

    //RADIATION HAZARD: _.reverse and TextField.set both cause mutations!!!
    const finalHeaders = _.chain(usefulHeaders)
        .reverse()
        .map(h => multipleHeaders[h] ? h.set("text", `${h} (${multipleHeaders[h]--})`) : h)
        .reverse()
        .value();

    const data = {
        headers: finalHeaders,
        data: filteredTail
    };

    const stats = calculateStats(filteredTail, mobPos, emPos, bDayPos);

    yield {progress: 3, message: 'fileup.calculatedstats'};
    yield {
        data: data,
        stats: stats,
        meta: {
            mobPos: mobPos,
            emPos: emPos,
            bDayPos: bDayPos
        }
    };
}

export {calculator as metaCalculator, calculateStats, initializer as calculatorInitializer};